import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiEdit2 } from 'react-icons/fi';
import { FaTrash } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import useEditableContent from '../hooks/useEditableContent';
import EditableModal from '../components/EditableModal';
import RichTextEditor from '../components/RichTextEditor';
import ConfirmModal from '../components/ConfirmModal';

// Styled components
const ExperienceContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 0;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 3rem;
  text-align: center;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const AddButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  border-radius: 2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const TimelineGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 2rem;
  padding: 2rem;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-left: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: var(--accent-gradient);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-primary);

    &::before {
      transform: scaleY(1);
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CardActions = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;

  ${TimelineItem}:hover & {
    opacity: 1;
  }
`;

const EditIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid var(--accent-primary);
  color: var(--accent-primary);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--accent-primary);
    color: white;
    transform: scale(1.05);
  }
`;

const DeleteIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ef4444;
    color: white;
    transform: scale(1.05);
  }
`;

const Year = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--accent-primary);
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Position = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const Company = styled.h3`
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--accent-primary);
  margin: 0;
`;

const Description = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin: 0;
`;

const LoadingContainer = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingText = styled.p`
  font-size: 1.25rem;
  color: var(--text-secondary);
`;

const EmptyMessage = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: var(--text-secondary);
  padding: 3rem;
`;

// Main Experience component
const Experience = () => {
  const { isAdmin } = useAuth();

  // Fetch experience highlights from database using custom hook
  const { 
    items: content, 
    loading,
    showModal,
    isCreating,
    formData,
    updateFormData,
    confirmOpen,
    itemToDelete,
    startCreate,
    startEdit,
    cancelEdit,
    save,
    deleteItem,
    confirmDelete,
    cancelDelete
  } = useEditableContent('experience', {
    page: 'experience',
    section: 'highlights',
  });

  if (loading) {
    return (
      <ExperienceContainer>
        <LoadingContainer>
          <LoadingText>Loading experience highlights...</LoadingText>
        </LoadingContainer>
      </ExperienceContainer>
    );
  }

  return (
    <ExperienceContainer>
      <ConfirmModal
        isOpen={confirmOpen}
        title="Delete Experience"
        message="Are you sure you want to delete this experience item? This action cannot be undone."
        onConfirm={() => deleteItem(itemToDelete)}
        onClose={cancelDelete}
      />

      <Container>
        <HeaderSection>
          <Title>Experience Highlights</Title>
          {isAdmin && (
            <AddButton onClick={() => startCreate({
              title: '',
              description: '',
              data: { year: new Date().getFullYear().toString(), company: '' }
            })}>
              Add Experience
            </AddButton>
          )}
        </HeaderSection>

        <TimelineGrid>
          {content && content.length > 0 ? (
            content.map((item, index) => (
              <TimelineItem
                key={item._id || item.id}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {isAdmin && (
                  <CardActions>
                    <EditIconButton onClick={() => startEdit(item)}>
                      <FiEdit2 />
                    </EditIconButton>
                    <DeleteIconButton onClick={() => confirmDelete(item)}>
                      <FaTrash />
                    </DeleteIconButton>
                  </CardActions>
                )}
                <Year>{item.data?.year || 'N/A'}</Year>
                <Content>
                  <Position>{item.title}</Position>
                  {item.data?.company && <Company>{item.data.company}</Company>}
                  <Description dangerouslySetInnerHTML={{ __html: item.description }} />
                </Content>
              </TimelineItem>
            ))
          ) : (
            <EmptyMessage>No experience highlights found.</EmptyMessage>
          )}
        </TimelineGrid>
      </Container>

      {/* Edit Modal */}
      <EditableModal
        isOpen={showModal}
        isCreating={isCreating}
        onClose={cancelEdit}
        onSave={save}
        title={isCreating ? 'Add Experience' : 'Edit Experience'}
        maxWidth="800px"
      >
        <label>Year</label>
        <input
          type="text"
          value={formData.data?.year || ''}
          onChange={e => updateFormData({ 
            data: { ...formData.data, year: e.target.value }
          })}
          placeholder="2024"
        />

        <label>Position/Role</label>
        <input
          type="text"
          value={formData.title || ''}
          onChange={e => updateFormData({ title: e.target.value })}
          placeholder="Senior Software Engineer"
        />

        <label>Company</label>
        <input
          type="text"
          value={formData.data?.company || ''}
          onChange={e => updateFormData({ 
            data: { ...formData.data, company: e.target.value }
          })}
          placeholder="Company Name"
        />

        <label>Description</label>
        <RichTextEditor
          value={formData.description || ''}
          onChange={(content) => updateFormData({ description: content })}
          placeholder="Describe your role and achievements..."
          height="250px"
        />
      </EditableModal>
    </ExperienceContainer>
  );
};

export default Experience;
